/**
 * useSubscriptionSync Hook
 *
 * Keeps subscription state in sync with auth state.
 *
 * - When a user signs in (or token refreshes), fetch latest subscription.
 * - When a user signs out, reset subscription state.
 *
 * This avoids requiring users to open Settings to see their Pro status.
 */

import { useEffect, useRef } from 'react';
import { useAuthStore } from '../store/authStore';
import { useSubscriptionStore } from '../store/subscriptionStore';
import { onAuthStateChange } from '../services/authService';

export function useSubscriptionSync(): void {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const isInitialized = useAuthStore((s) => s.isInitialized);

  const lastUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isInitialized) return;

    // Sign-out (or auth not available)
    if (!userId) {
      lastUserIdRef.current = null;
      useSubscriptionStore.getState().reset();
      return;
    }

    // First sign-in OR user switch
    if (lastUserIdRef.current !== userId) {
      lastUserIdRef.current = userId;
      void useSubscriptionStore.getState().fetchSubscription();
    }

    // When the user returns from Stripe Checkout (browser) back to the app,
    // the app window regains focus/visibility. Use that as a natural sync point.
    const handleFocus = () => {
      void useSubscriptionStore.getState().fetchSubscription();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void useSubscriptionStore.getState().fetchSubscription();
      }
    };

    // Also fetch on auth transitions (e.g. TOKEN_REFRESHED) while user stays signed in.
    const unsubscribeAuth = onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        void useSubscriptionStore.getState().fetchSubscription();
      }
      if (event === 'SIGNED_OUT') {
        useSubscriptionStore.getState().reset();
      }
    });

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      unsubscribeAuth();
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userId, isInitialized]);
}
